/**
 * Single source of truth for the Andrew Pawlak author entity.
 *
 * Everything that renders or describes the author — bylines, the author bio
 * box, schema Person nodes, Article author references — imports from here so
 * the name, title, headshot, LinkedIn URL and bio can never drift apart
 * across templates. Values below are owner-confirmed facts only (see memory:
 * entity facts, claim-provenance rules).
 */

export interface AuthorProfile {
  name: string;
  /** Short byline title (fits inline under a headline). */
  title: string;
  /** Full title used on the author page and in Person schema. */
  fullTitle: string;
  /** Headshot in /public. */
  image: string;
  /** Canonical LinkedIn profile — the one Andrew gave us. No trailing slash. */
  linkedin: string;
  /** Path to the on-site author page (the entity hub bylines link to). */
  pagePath: string;
  /** One-paragraph bio for byline boxes. */
  bioShort: string;
}

export const ANDREW_PAWLAK: AuthorProfile = {
  name: 'Andrew Pawlak',
  title: 'Co-Founder & CEO, LeadPops (rebel iQ)',
  fullTitle: 'Co-Founder & CEO, rebel iQ, Inc. (LeadPops)',
  image: '/Andrew-Pawlak-LeadPops-rebeliQ.jpeg',
  linkedin: 'https://www.linkedin.com/in/andrewpawlak',
  pagePath: '/author/andrew-pawlak',
  bioShort:
    'Co-Founder & CEO of LeadPops by rebel iQ. Author of The Mortgage Marketing Manifesto and Leads Apocalypse. 22+ years in mortgage marketing — he co-founded 220 Marketing, then rebel iQ, the company behind the lead generation systems for Zillow and Bankrate. LeadPops now serves 5,247+ mortgage professionals who have generated 3.2M+ leads.',
};

const KNOWN_AUTHORS: AuthorProfile[] = [ANDREW_PAWLAK];

/** Returns the profile for a frontmatter `author` string, or null for guest/team authors. */
export function getAuthorProfile(author?: string | null): AuthorProfile | null {
  if (!author) return null;
  const normalized = author.trim().toLowerCase();
  return KNOWN_AUTHORS.find((a) => a.name.toLowerCase() === normalized) ?? null;
}
